import { computed, watch, inject, ref, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import { useInstanceStore } from '@/common/stores/instance';
import { DATABASE_MULTI_VIEW_TYPES } from '@/database/utils/define';
import { type CurrentInstance } from '@/common/stores/instance';
import { OVERVIEW_TYPE } from '@/database/utils/overview.define';
import type { BreadCrumbMenuInfo, BreadCrumbMode } from '../breadCrumbs.types';
import { BREAD_CRUMBS_CHANGE_FAVORITE_KEY } from '../breadCrumbs.define';

export interface Props {
  menu: BreadCrumbMenuInfo;
  mode?: BreadCrumbMode;
  currentInstance?: CurrentInstance;
  currentMultiViewExpandedMenu?: string;
}

export interface Emit {
  (e: 'instance-multi-view-expand', evt: MouseEvent): void;
  (e: 'link-clicked'): void;
}

// Instance
const useInstanceElementLink = (isCurrent: Ref<boolean>, props: Props, emit: Emit) => {
  const { changeInstance } = useInstanceStore();

  const isMultiViewExpanded = ref(false);

  const isInstanceClickBlocked = computed(() => {
    return (
      (props.menu.instanceType === 'multiView' && !DATABASE_MULTI_VIEW_TYPES[props.menu.dbType!]) ||
      (props.menu.instanceType === 'clusterView' && props.menu.children?.length)
    );
  });

  const onInstanceClick = () => {
    if (
      (isCurrent.value &&
        props.menu.instanceType === props.currentInstance?.currentDbInstanceType) ||
      isInstanceClickBlocked.value
    ) {
      return;
    }

    changeInstance(props.menu);
  };

  const onInstanceMultiViewExpand = (evt: MouseEvent) => {
    emit('instance-multi-view-expand', evt);
  };

  watch(
    () => props.currentMultiViewExpandedMenu,
    (newValue) => {
      isMultiViewExpanded.value = newValue === props.menu.value;
    },
    { immediate: true },
  );

  return {
    isMultiViewExpanded,
    isInstanceClickBlocked,
    onInstanceClick,
    onInstanceMultiViewExpand,
  };
};

// Dashboard
const useDashboardElementLink = (props: Props) => {
  const changeFavorite = inject(BREAD_CRUMBS_CHANGE_FAVORITE_KEY, async () => {}); // breadCrumbs.setup.ts에서 제공하는 함수

  const onFavoriteClick = async () => {
    await changeFavorite(props.menu);
  };

  return {
    onFavoriteClick,
  };
};

export const getLinkWrapperTop = (evt: MouseEvent) => {
  if (evt.target instanceof HTMLElement) {
    const linkWrapper = evt.target.closest('.link-wrapper')?.getBoundingClientRect();
    return linkWrapper?.top ?? 0;
  }
  return 0;
};

export const setup = (props: Props, emit: Emit) => {
  const route = useRoute();
  const onLinkClick = () => {
    emit('link-clicked');
  };
  const isCurrent = computed(() => {
    if (props.mode === 'dashboard') {
      return false;
    }

    // 쿼리를 포함한 leaf 메뉴(예: Workloads 타입 탭 /…/workload?type=deployments)는
    // 라우트 레코드가 동일해 router-link active가 형제 항목까지 켜진다.
    // fullPath 완전일치로 판정하면 다른 쿼리 파라미터(시간범위 등)가 하나라도 붙을 때 활성이 풀리므로,
    // 경로(pathname) + 메뉴가 지정한 쿼리 파라미터만 부분일치로 확인한다.
    if (props.menu.path?.includes('?')) {
      const [menuPath, menuQuery = ''] = props.menu.path.split('?');
      if (route.path.toLocaleLowerCase() !== menuPath.toLocaleLowerCase()) {
        return false;
      }
      return [...new URLSearchParams(menuQuery).entries()].every(
        ([key, value]) =>
          (route.query[key]?.toString() ?? '').toLocaleLowerCase() === value.toLocaleLowerCase(),
      );
    }

    if (props.mode === 'common' && props.menu?.subMenuList?.length) {
      const routePath = route.path.toLocaleLowerCase();
      const currentMenu = props.menu.value?.replaceAll('_', '/')?.toLocaleLowerCase();

      if (routePath.includes(currentMenu!)) return true;

      const matchesItem = (item: { path?: string; value?: string }) =>
        (item.path && routePath.includes(item.path.toLocaleLowerCase())) ||
        (item.value && routePath.includes(item.value.replaceAll('_', '/').toLocaleLowerCase()));

      return props.menu.subMenuList.some(
        (subMenu) => matchesItem(subMenu) || subMenu.children?.some(matchesItem),
      );
    }

    return (
      route.path.includes(props.menu.value!) ||
      props.menu.value === props.currentInstance?.multiView.id ||
      props.menu.value === props.currentInstance?.singleView.id
    );
  });

  const instanceTagName = computed(() => {
    switch (props.menu.instanceType) {
      case OVERVIEW_TYPE.MULTI_VIEW:
        return 'multi';
      case OVERVIEW_TYPE.CLUSTER_VIEW: {
        if (props.menu.children?.length) {
          return 'Cluster Group';
        }
        return 'cluster';
      }
      case OVERVIEW_TYPE.SINGLE_VIEW:
        return 'single';
      default:
        return '';
    }
  });

  return {
    isCurrent,
    instanceTagName,
    onLinkClick,
    ...useInstanceElementLink(isCurrent, props, emit),
    ...useDashboardElementLink(props),
  };
};
